import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { OnboardingProvider } from "./contexts/OnboardingContext";
import SavedLoads from "./pages/SavedLoads";
import AIVoiceAgent from "./pages/AIVoiceAgent";
import AINotifications from "./pages/AINotifications";
import Profile from "./pages/Profile";
import Subscribe from "./pages/Subscribe";
import Documents from "./pages/Documents";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import Login from "./pages/Login";
import AuthCallback from "./components/AuthCallback";
import BackhaulResults from "./pages/BackhaulResults";
import Team from "./pages/Team.js";
import Carriers from "./pages/Carriers";
import MyLoads from "./pages/MyLoads";
import QuickStart from "./pages/QuickStart";
import ContactUs from "./pages/ContactUs";
import Unsubscribe from "./pages/Unsubscribe";
import Onboarding from "./pages/Onboarding";
import BookIt from "./pages/BookIt/BookIt";
import GmailConnect from "./pages/GmailConnect/GmailConnect";
import SubscriptionManage from "./pages/BookIt/SubscriptionManage";
import AdminDashboard from "./pages/Admin/AdminDashboard";
import { isAdmin } from "./utils/adminEmails";
import DocumentUploadPage from "./pages/DocumentUploadPage";
import MockCarrierPortal from "./pages/MockCarrierPortal";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { usePageTracking } from "./utils/useAnalytics";
import { DemoLoadProvider } from "./contexts/DemoLoadContext.js";
import DashboardLayout from "./components/DashboardLayout/DashboardLayout";
import NewLoadSearch from "./pages/NewLoadSearch";
import { isOnboardingComplete } from "./utils/onboardingCheck";

function ProtectedRoute({
  children,
  requiredSubscription = [],
  requiredAccountType = null,
}) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(false);
  const [shouldRedirectToOnboarding, setShouldRedirectToOnboarding] =
    useState(false);

  // Check if this is an onboarding path
  const isOnboardingPath = location.pathname.startsWith("/onboarding");

  // Reset redirect state when conditions change
  useEffect(() => {
    if (isOnboardingPath || user?.dot_authorized) {
      setShouldRedirectToOnboarding(false);
    }
  }, [isOnboardingPath, user?.dot_authorized]);

  // Check onboarding completion when user doesn't have dot_authorized
  useEffect(() => {
    const checkOnboarding = async () => {
      // Skip check if:
      // - Still loading
      // - No user
      // - Already on onboarding path
      // - Already on profile path (let them access it)
      // - User has dot_authorized (onboarding complete)
      if (
        loading ||
        !user ||
        isOnboardingPath ||
        location.pathname === "/profile" ||
        user.dot_authorized
      ) {
        setIsCheckingOnboarding(false);
        setShouldRedirectToOnboarding(false);
        return;
      }

      // User doesn't have dot_authorized - check if onboarding is actually complete
      setIsCheckingOnboarding(true);
      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          setIsCheckingOnboarding(false);
          return;
        }

        const complete = await isOnboardingComplete(user, token);

        // If onboarding is not complete, redirect to onboarding
        if (!complete) {
          setShouldRedirectToOnboarding(true);
        } else {
          setShouldRedirectToOnboarding(false);
        }
      } catch (err) {
        console.error("Error checking onboarding completion:", err);
        // On error, assume incomplete and redirect to onboarding
        setShouldRedirectToOnboarding(true);
      } finally {
        setIsCheckingOnboarding(false);
      }
    };

    checkOnboarding();
  }, [user, loading, isOnboardingPath, location.pathname]);

  if (loading || isCheckingOnboarding) {
    return <div>Loading...</div>;
  }

  // Require authentication for onboarding (but allow incomplete onboarding)
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Allow onboarding paths to proceed without DOT authorization or subscription checks
  if (isOnboardingPath) {
    return children;
  }

  // Redirect to onboarding if it's incomplete
  if (shouldRedirectToOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  // If onboarding is complete but dot_authorized is false (edge case), redirect to profile
  if (!user.dot_authorized && location.pathname !== "/profile") {
    return <Navigate to="/profile" replace />;
  }
  const userSubscription = user.subscription_type || 0;
  // Handle both array and number formats for backward compatibility
  const requiredTypes = Array.isArray(requiredSubscription)
    ? requiredSubscription
    : typeof requiredSubscription === "number" && requiredSubscription >= 0
      ? [requiredSubscription] // Single number becomes array with that number
      : requiredSubscription > 0
        ? [1, 2]
        : [0, 1, 2]; // Legacy: If > 0, require trial or premium

  if (requiredTypes.length > 0 && !requiredTypes.includes(userSubscription)) {
    return <Navigate to="/subscribe" replace />;
  }

  if (requiredAccountType && user.account_type !== requiredAccountType) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <div>Loading...</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (!isAdmin(user)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes({ showGlobalOverlay, setShowGlobalOverlay }) {
  const { theme } = useTheme();
  const { user } = useAuth();

  // Automatically track page views with user info
  usePageTracking(user);

  useEffect(() => {
    document.body.className = "";
    document.body.classList.add(`theme-${theme}`);
    localStorage.setItem("theme", theme);
  }, [theme]);
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route
        path="/book-it/subscription/:token"
        element={<SubscriptionManage />}
      />
      <Route path="/book-it/:token" element={<BookIt />} />
      <Route path="/gmail/:token" element={<GmailConnect />} />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        }
      />
      <Route path="/upload/:token" element={<DocumentUploadPage />} />

      <Route path="/mock-carrier-portal" element={<MockCarrierPortal />} />

      <Route
        path="/analytics"
        element={<Navigate to="/analytics/hotshot" replace />}
      />

      {/* Onboarding route - single page with step-based content */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingProvider>
              <Onboarding />
            </OnboardingProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/load-search"
        element={
          <ProtectedRoute
            requiredAccountType={["dispatcher", "carrier"]}
            requiredSubscription={[1, 2]}
          >
            <DashboardLayout showMobileHeader={false}>
              <NewLoadSearch />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription-success"
        element={
          <ProtectedRoute>
            <DashboardLayout contentBackground="bg-[#101820]">
              <SubscriptionSuccess />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/subscribe"
        element={
          <ProtectedRoute>
            <DashboardLayout contentBackground="bg-[#101820]">
              <Subscribe />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/saved-loads"
        element={
          <ProtectedRoute requiredSubscription={[1, 2]}>
            <DashboardLayout contentBackground="bg-[#101820]">
              <SavedLoads />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/load-results"
        element={
          <ProtectedRoute requiredSubscription={[1, 2]}>
            <DashboardLayout
              contentBackground="bg-[#101820]"
              showMobileHeader={false}
            >
              <NewLoadSearch />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/team"
        element={
          <ProtectedRoute
            requiredAccountType="carrier"
            requiredSubscription={[1, 2]}
          >
            <DashboardLayout contentBackground="bg-[#101820]">
              <Team />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/carriers"
        element={
          <ProtectedRoute
            requiredAccountType="dispatcher"
            requiredSubscription={[1, 2]}
          >
            <DashboardLayout contentBackground="bg-[#101820]">
              <Carriers />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute requiredSubscription={[1, 2]}>
            <DashboardLayout showMobileHeader={false}>
              {user?.account_type === "driver" ? (
                <Navigate to="/my-loads" replace />
              ) : (
                <NewLoadSearch />
              )}
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/ai-voice-agent"
        element={
          <ProtectedRoute
            requiredAccountType="carrier"
            requiredSubscription={[1, 2]}
          >
            <DashboardLayout contentBackground="bg-[#101820]">
              <AIVoiceAgent />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-notifications"
        element={
          <ProtectedRoute
            requiredAccountType="carrier"
            requiredSubscription={2}
          >
            <DashboardLayout contentBackground="bg-[#101820]">
              <AINotifications />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/backhaul-results"
        element={
          <ProtectedRoute requiredSubscription={[1, 2]}>
            <DashboardLayout contentBackground="bg-[#101820]">
              <BackhaulResults />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <DashboardLayout contentBackground="bg-[#101820]">
              <Profile />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-loads"
        element={
          <ProtectedRoute requiredAccountType="driver">
            <DashboardLayout contentBackground="bg-[#101820]">
              <MyLoads />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/quick-start"
        element={
          <ProtectedRoute>
            <DashboardLayout contentBackground="bg-[#101820]">
              <QuickStart />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contact-us"
        element={
          <ProtectedRoute>
            <DashboardLayout contentBackground="bg-[#101820]">
              <ContactUs />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute requiredSubscription={[1, 2]}>
            <DashboardLayout contentBackground="bg-[#101820]">
              <Documents />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  const [showGlobalOverlay, setShowGlobalOverlay] = useState(false);
  return (
    <AuthProvider>
      <ThemeProvider>
        <DemoLoadProvider>
          <div className="App">
            {showGlobalOverlay && (
              <div
                className="global-overlay"
                onClick={() => setShowGlobalOverlay(false)}
              />
            )}
            <AppRoutes
              showGlobalOverlay={showGlobalOverlay}
              setShowGlobalOverlay={setShowGlobalOverlay}
            />
          </div>
        </DemoLoadProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
